import { textIn } from '@croissants/contracts/projections';
import {
  createContext,
  type Dispatch,
  type JSX,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { readJson, writeJson } from '../lib/storage';
import {
  type BasketAction,
  type BasketState,
  basketReducer,
  EMPTY_BASKET,
  isBasketState,
  itemCount,
  quantityOf,
} from './basket';
import { useLocale } from './locale';

/**
 * The basket, wired to React and to storage.
 *
 * All the rules live in the pure reducer next door; this file only holds it and
 * survives a reload. Surviving a reload matters more here than it looks: the
 * journey happens on a phone in an unfamiliar flat, and the browser will happily
 * discard a tab that was backgrounded to answer a call.
 *
 * The basket is scoped to the QR token, so the code in a different apartment
 * never opens onto someone else's basket.
 */

const STORAGE_PREFIX = 'cib.basket.';

export interface BasketContextValue {
  readonly basket: BasketState;
  readonly dispatch: Dispatch<BasketAction>;
  readonly count: number;
  readonly quantityOf: (itemId: string) => number;
}

const BasketContext = createContext<BasketContextValue | null>(null);

export function BasketProvider({
  children,
  scope,
}: {
  children: ReactNode;
  /** The QR token, so one phone can hold one basket per apartment. */
  scope: string | null;
}): JSX.Element {
  const storageKey = `${STORAGE_PREFIX}${scope ?? 'anonymous'}`;

  const [basket, dispatch] = useReducer(
    basketReducer,
    storageKey,
    (key) => readJson<BasketState>(key, isBasketState) ?? EMPTY_BASKET,
  );

  useEffect(() => {
    writeJson(storageKey, basket);
  }, [storageKey, basket]);

  const value = useMemo<BasketContextValue>(
    () => ({
      basket,
      dispatch,
      count: itemCount(basket),
      quantityOf: (itemId: string) => quantityOf(basket, itemId),
    }),
    [basket],
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export function useBasket(): BasketContextValue {
  const value = useContext(BasketContext);
  if (value === null) throw new Error('useBasket must be used inside <BasketProvider>');
  return value;
}

/**
 * Teaches the basket the names of the items it holds, from a catalogue that has
 * just loaded.
 *
 * Adding an item records its name, but two baskets never went through that path:
 * one restored from storage before names were kept, and one rebuilt from a past
 * order, which arrives as ids and quantities (GST-010). Both reach the review
 * screen, and the review screen may be the one place the catalogue call fails.
 * So every screen that does hold a catalogue writes what it knows back.
 *
 * Idempotent by construction: the reducer returns the identical state when no
 * label changed, so this cannot loop with the effect that persists the basket.
 *
 * The labels are learned in the READER'S language and RE-learned when it
 * changes (NFR-006). The shelf carries every shipped language beside the served
 * name, so a guest who switches language sees their own basket lines move with
 * the rest of the screen; before this the basket kept the words it was taught
 * on the way in, and a French screen listed English croissants until the line
 * was touched again.
 */
export function useRememberItemNames(
  items:
    | readonly {
        readonly id: string;
        readonly name: string;
        readonly nameByLocale?: Readonly<Record<string, string>>;
      }[]
    | undefined,
): void {
  const { dispatch } = useBasket();
  const { locale } = useLocale();
  useEffect(() => {
    if (items === undefined || items.length === 0) return;
    dispatch({
      type: 'NAME_LINES',
      names: Object.fromEntries(
        items.map((item) => [item.id, textIn(locale, item.nameByLocale, item.name)]),
      ),
    });
  }, [items, locale, dispatch]);
}
